// ===========================================
// Newsletter Hooks
// ===========================================
// React Query hooks for newsletter and blog features

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPublicBlogPosts,
  getPublicBlogPost,
  getPortalNewsletters,
  getPortalNewsletter,
  createNewsletter,
  getNewsletters,
  getNewsletter,
  updateNewsletter,
  deleteNewsletter,
  duplicateNewsletter,
  publishNewsletter,
  scheduleNewsletter,
  unpublishNewsletter,
  addSection,
  updateSection,
  deleteSection,
  reorderSections,
  getRecipientPreview,
  sendPreviewEmail,
  sendNewsletter,
  getNewsletterStats,
  getDetailedAnalytics,
} from '../api/newsletter.api';
import {
  CreateNewsletterData,
  UpdateNewsletterData,
  ScheduleNewsletterData,
  CreateSectionData,
  UpdateSectionData,
  ReorderSectionsData,
  SendPreviewData,
  NewsletterFilters,
  PublicBlogFilters,
} from '../types/newsletter.types';

// ===========================================
// QUERY KEY FACTORY
// ===========================================

export const newsletterKeys = {
  all: ['newsletter'] as const,
  // Admin newsletters
  lists: () => [...newsletterKeys.all, 'lists'] as const,
  list: (filters?: NewsletterFilters) => [...newsletterKeys.lists(), filters] as const,
  details: () => [...newsletterKeys.all, 'details'] as const,
  detail: (newsletterId: string) => [...newsletterKeys.details(), newsletterId] as const,
  // Portal newsletters (parent view)
  portal: () => [...newsletterKeys.all, 'portal'] as const,
  portalList: (filters?: NewsletterFilters) => [...newsletterKeys.portal(), 'list', filters] as const,
  portalDetail: (slug: string) => [...newsletterKeys.portal(), 'detail', slug] as const,
  // Public blog
  publicBlog: () => [...newsletterKeys.all, 'public-blog'] as const,
  publicBlogList: (schoolId: string, filters?: PublicBlogFilters) =>
    [...newsletterKeys.publicBlog(), 'list', schoolId, filters] as const,
  publicBlogDetail: (schoolId: string, slug: string) =>
    [...newsletterKeys.publicBlog(), 'detail', schoolId, slug] as const,
  // Distribution
  recipients: (newsletterId: string) => [...newsletterKeys.all, 'recipients', newsletterId] as const,
  // Analytics
  analytics: () => [...newsletterKeys.all, 'analytics'] as const,
  stats: (newsletterId: string) => [...newsletterKeys.analytics(), 'stats', newsletterId] as const,
  detailed: (newsletterId: string) => [...newsletterKeys.analytics(), 'detailed', newsletterId] as const,
};

// ===========================================
// PUBLIC BLOG HOOKS
// ===========================================

/**
 * Hook to fetch public blog posts
 */
export function usePublicBlogPosts(schoolId: string, filters?: PublicBlogFilters) {
  return useQuery({
    queryKey: newsletterKeys.publicBlogList(schoolId, filters),
    queryFn: () => getPublicBlogPosts(schoolId, filters),
    enabled: !!schoolId,
  });
}

/**
 * Hook to fetch a single public blog post
 */
export function usePublicBlogPost(schoolId: string, slug: string) {
  return useQuery({
    queryKey: newsletterKeys.publicBlogDetail(schoolId, slug),
    queryFn: () => getPublicBlogPost(schoolId, slug),
    enabled: !!schoolId && !!slug,
  });
}

// ===========================================
// PORTAL NEWSLETTER HOOKS (Parent View)
// ===========================================

/**
 * Hook to fetch portal newsletters
 */
export function usePortalNewsletters(filters?: NewsletterFilters) {
  return useQuery({
    queryKey: newsletterKeys.portalList(filters),
    queryFn: () => getPortalNewsletters(filters),
  });
}

/**
 * Hook to fetch a single portal newsletter
 */
export function usePortalNewsletter(slug: string) {
  return useQuery({
    queryKey: newsletterKeys.portalDetail(slug),
    queryFn: () => getPortalNewsletter(slug),
    enabled: !!slug,
  });
}

// ===========================================
// ADMIN NEWSLETTER CRUD HOOKS
// ===========================================

/**
 * Hook to fetch all newsletters (admin)
 */
export function useNewsletters(filters?: NewsletterFilters) {
  return useQuery({
    queryKey: newsletterKeys.list(filters),
    queryFn: () => getNewsletters(filters),
  });
}

/**
 * Hook to fetch a single newsletter
 */
export function useNewsletter(newsletterId: string) {
  return useQuery({
    queryKey: newsletterKeys.detail(newsletterId),
    queryFn: () => getNewsletter(newsletterId),
    enabled: !!newsletterId,
  });
}

/**
 * Hook to create a newsletter
 */
export function useCreateNewsletter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNewsletterData) => createNewsletter(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.lists() });
    },
  });
}

/**
 * Hook to update a newsletter
 */
export function useUpdateNewsletter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      newsletterId,
      data,
    }: {
      newsletterId: string;
      data: UpdateNewsletterData;
    }) => updateNewsletter(newsletterId, data),
    onSuccess: (_, { newsletterId }) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.lists() });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.detail(newsletterId) });
    },
  });
}

/**
 * Hook to delete a newsletter
 */
export function useDeleteNewsletter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newsletterId: string) => deleteNewsletter(newsletterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.lists() });
    },
  });
}

/**
 * Hook to duplicate a newsletter
 */
export function useDuplicateNewsletter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newsletterId: string) => duplicateNewsletter(newsletterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.lists() });
    },
  });
}

// ===========================================
// PUBLISHING & SCHEDULING HOOKS
// ===========================================

/**
 * Hook to publish a newsletter
 */
export function usePublishNewsletter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newsletterId: string) => publishNewsletter(newsletterId),
    onSuccess: (_, newsletterId) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.lists() });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.detail(newsletterId) });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.portal() });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.publicBlog() });
    },
  });
}

/**
 * Hook to schedule a newsletter
 */
export function useScheduleNewsletter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      newsletterId,
      data,
    }: {
      newsletterId: string;
      data: ScheduleNewsletterData;
    }) => scheduleNewsletter(newsletterId, data),
    onSuccess: (_, { newsletterId }) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.lists() });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.detail(newsletterId) });
    },
  });
}

/**
 * Hook to unpublish a newsletter
 */
export function useUnpublishNewsletter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newsletterId: string) => unpublishNewsletter(newsletterId),
    onSuccess: (_, newsletterId) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.lists() });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.detail(newsletterId) });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.portal() });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.publicBlog() });
    },
  });
}

// ===========================================
// SECTION MANAGEMENT HOOKS
// ===========================================

/**
 * Hook to add a section
 */
export function useAddSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      newsletterId,
      data,
    }: {
      newsletterId: string;
      data: CreateSectionData;
    }) => addSection(newsletterId, data),
    onSuccess: (_, { newsletterId }) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.detail(newsletterId) });
    },
  });
}

/**
 * Hook to update a section
 */
export function useUpdateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      newsletterId,
      sectionId,
      data,
    }: {
      newsletterId: string;
      sectionId: string;
      data: UpdateSectionData;
    }) => updateSection(newsletterId, sectionId, data),
    onSuccess: (_, { newsletterId }) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.detail(newsletterId) });
    },
  });
}

/**
 * Hook to delete a section
 */
export function useDeleteSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      newsletterId,
      sectionId,
    }: {
      newsletterId: string;
      sectionId: string;
    }) => deleteSection(newsletterId, sectionId),
    onSuccess: (_, { newsletterId }) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.detail(newsletterId) });
    },
  });
}

/**
 * Hook to reorder sections
 */
export function useReorderSections() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      newsletterId,
      data,
    }: {
      newsletterId: string;
      data: ReorderSectionsData;
    }) => reorderSections(newsletterId, data),
    onSuccess: (_, { newsletterId }) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.detail(newsletterId) });
    },
  });
}

// ===========================================
// DISTRIBUTION HOOKS
// ===========================================

/**
 * Hook to get recipient preview
 */
export function useRecipientPreview(newsletterId: string) {
  return useQuery({
    queryKey: newsletterKeys.recipients(newsletterId),
    queryFn: () => getRecipientPreview(newsletterId),
    enabled: !!newsletterId,
  });
}

/**
 * Hook to send preview email
 */
export function useSendPreviewEmail() {
  return useMutation({
    mutationFn: ({
      newsletterId,
      data,
    }: {
      newsletterId: string;
      data: SendPreviewData;
    }) => sendPreviewEmail(newsletterId, data),
  });
}

/**
 * Hook to send newsletter to all recipients
 */
export function useSendNewsletter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newsletterId: string) => sendNewsletter(newsletterId),
    onSuccess: (_, newsletterId) => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.lists() });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.detail(newsletterId) });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.stats(newsletterId) });
    },
  });
}

// ===========================================
// ANALYTICS HOOKS
// ===========================================

/**
 * Hook to fetch newsletter stats
 */
export function useNewsletterStats(newsletterId: string) {
  return useQuery({
    queryKey: newsletterKeys.stats(newsletterId),
    queryFn: () => getNewsletterStats(newsletterId),
    enabled: !!newsletterId,
  });
}

/**
 * Hook to fetch detailed analytics
 */
export function useDetailedAnalytics(newsletterId: string) {
  return useQuery({
    queryKey: newsletterKeys.detailed(newsletterId),
    queryFn: () => getDetailedAnalytics(newsletterId),
    enabled: !!newsletterId,
  });
}
